import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function AmazonCallback() {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing Amazon authorization...');

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        if (error) {
          setStatus('error');
          setMessage(`Amazon authorization failed: ${error}`);
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('Authorization code not received from Amazon');
          return;
        }

        // Exchange code for token
        const response = await apiRequest('/api/amazon/auth/callback', {
          method: 'POST',
          data: { code, state }
        });

        const result = await response.json();

        if (result.success) {
          setStatus('success');
          setMessage('Amazon Business API connected successfully!');
          
          // Redirect to Amazon Business page after 2 seconds
          setTimeout(() => {
            setLocation('/admin/amazon');
          }, 2000);
        } else {
          setStatus('error');
          setMessage(result.error || 'Failed to connect to Amazon Business API');
        }
      } catch (error) {
        console.error('Callback processing error:', error);
        setStatus('error');
        setMessage('An error occurred while processing the authorization');
      }
    };

    processCallback();
  }, [setLocation]);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <Loader2 className="h-8 w-8 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="h-8 w-8 text-green-500" />;
      case 'error':
        return <XCircle className="h-8 w-8 text-red-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800 border-slate-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {getStatusIcon()}
          </div>
          <CardTitle className="text-white text-xl">
            Amazon Business Integration
          </CardTitle>
          <CardDescription className="text-gray-400">
            {status === 'processing' && 'Processing your authorization...'}
            {status === 'success' && 'Successfully connected!'}
            {status === 'error' && 'Connection failed'}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <p className={`mb-4 ${getStatusColor()}`}>
            {message}
          </p>
          
          {status === 'success' && (
            <div className="text-sm text-gray-400">
              Redirecting to Amazon Business dashboard...
            </div>
          )}
          
          {status === 'error' && (
            <div className="space-y-3">
              <Button 
                onClick={() => setLocation('/admin/amazon')}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                Return to Amazon Business
              </Button>
              <Button 
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full border-slate-600 text-gray-300 hover:bg-slate-700"
              >
                Try Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}